import { formatInstant } from "src/domains/shared/utils/datetime";
import {
  hasHistoryType,
  HistoryEvent,
  KNOWN_HISTORY_TYPES,
  KnownHistoryType,
} from "./projectHistory.model";

/**
 * 문장 조립용 순수 셀렉터 — t() 호출은 두지 않는다(i18n 은 UI 계층 소유).
 * 여기서는 키/폴백 판정만 하고, 미지 type 은 null 을 반환해 호출측이
 * raw 문자열을 쓰게 한다(missingKeyHandler 리포팅 노이즈 방지 — design.md D7).
 */

const isKnownHistoryType = (type: string): type is KnownHistoryType =>
  (KNOWN_HISTORY_TYPES as readonly string[]).includes(type);

/** 18종 닫힌 Record — 종류 추가 시 KNOWN_HISTORY_TYPES 와 함께 여기·i18n 리소스만 갱신한다. */
const HISTORY_TYPE_LABEL_KEYS: Record<KnownHistoryType, string> = {
  project: "myPage.history.type.project",
  time_project: "myPage.history.type.time_project",
  member: "myPage.history.type.member",
  annotation: "myPage.history.type.annotation",
  annotation_comment: "myPage.history.type.annotation_comment",
  annotation_comment_image: "myPage.history.type.annotation_comment_image",
  category: "myPage.history.type.category",
  vector: "myPage.history.type.vector",
  clipping: "myPage.history.type.clipping",
  worksite_zone: "myPage.history.type.worksite_zone",
  geolog: "myPage.history.type.geolog",
  walk_session: "myPage.history.type.walk_session",
  layer: "myPage.history.type.layer",
  drawing_mesh: "myPage.history.type.drawing_mesh",
  folder: "myPage.history.type.folder",
  project_equipment: "myPage.history.type.project_equipment",
  height_profile_record: "myPage.history.type.height_profile_record",
  height_profile_export: "myPage.history.type.height_profile_export",
};

/**
 * history.type 의 i18n 라벨 키 판정.
 * 알려진 18종이면 키를, 미지 type(열린 어휘 확장)이면 null 을 반환한다 —
 * 호출측은 null 이면 t() 없이 raw type 문자열을 라벨로 쓴다.
 */
export const resolveHistoryTypeLabelKey = (type: string): string | null =>
  isKnownHistoryType(type) ? HISTORY_TYPE_LABEL_KEYS[type] : null;

const HANGUL_SYLLABLE_START = 0xac00;
const HANGUL_SYLLABLE_END = 0xd7a3;
const JONGSEONG_COUNT = 28;

/**
 * 한국어 목적격 조사(을/를) 자동 선택 — ko 로케일의 수정(update) 문장 종류 라벨 전용.
 * 마지막 글자가 한글 음절이면 받침 유무로 을/를을 고르고,
 * 한글이 아니면(미지 type raw 문자열·영문 라벨 등) "을(를)" 병기로 폴백한다.
 * 한국어 외 로케일 문장에는 쓰지 않는다 — 현재 소비처는 ko `myPage.history.sentence.update` 뿐이다.
 */
export const withKoreanObjectParticle = (word: string): string => {
  const lastChar = word.charCodeAt(word.length - 1);
  const isHangulSyllable =
    !Number.isNaN(lastChar) &&
    lastChar >= HANGUL_SYLLABLE_START &&
    lastChar <= HANGUL_SYLLABLE_END;
  if (!isHangulSyllable) {
    return `${word}을(를)`;
  }
  const hasJongseong = (lastChar - HANGUL_SYLLABLE_START) % JONGSEONG_COUNT > 0;
  return `${word}${hasJongseong ? "을" : "를"}`;
};

/** annotation name 정규화 wire: {"title":…,"issue":…} — 부재 키는 null. */
const parseAnnotationName = (rawName: string): string | null => {
  try {
    const parsed: unknown = JSON.parse(rawName);
    if (typeof parsed !== "object" || parsed === null) return null;

    const { title, issue } = parsed as { title?: unknown; issue?: unknown };
    if (typeof title === "string" && title.length > 0) return title;
    if (typeof issue === "string" && issue.length > 0) return issue;
    return null;
  } catch {
    // 레거시 비정형 name — JSON 이 아니면 원문을 그대로 쓴다
    return null;
  }
};

/**
 * 대상 표시 이름 판정 — name 은 기록 시점 스냅샷이라 삭제된 대상(history.id null)도 표시된다.
 * annotation 은 stringified JSON 을 title → issue → 원문 순으로 폴백하고,
 * 그 외 type 은 name 을 그대로 반환한다(회차 일자 감싸기 등 문구 장식은 UI 계층 소유).
 */
export const resolveHistoryDisplayName = (event: HistoryEvent): string => {
  const { name } = event.history;
  if (!hasHistoryType(event.history, "annotation")) return name;
  return parseAnnotationName(name) ?? name;
};

/**
 * 탈퇴 이탈 이벤트 판정 — `member` 의 `delete` 에서 행위자와 대상이 같은 사람(id 가 둘 다 있고 같음)이면
 * 회원 탈퇴로 생긴 이탈이다. 멤버 제거 API 가 본인 지목을 거부하므로 이 조합은 탈퇴에서만 생긴다
 * (backend-api `timeline_v1_projects__project_id__history_get` 의 `history.name` 설명). 주인이 멤버를
 * 내보낸 기록은 actor 와 대상이 다르다. id 는 soft reference 라 탈퇴로 회원 행이 지워진 뒤에도 남는다 —
 * null 은 가입 전 초대처럼 대상 번호가 없는 줄이고, 그런 줄은 판정하지 않는다.
 */
export const isMemberWithdrawalEvent = (event: HistoryEvent): boolean =>
  event.action === "delete" &&
  hasHistoryType(event.history, "member") &&
  event.actor.id !== null &&
  event.history.id !== null &&
  event.actor.id === event.history.id;

/** 회차 귀속 뱃지 판정 결과 — UI 가 kind 로 i18n 템플릿(촬영 일자 vs 회차 id fallback)을 가른다. */
export type HistoryTimeProjectBadge =
  | { kind: "timestamp"; value: string }
  | { kind: "timeProjectId"; value: number };

/**
 * 회차 귀속 뱃지 판정 — 비귀속 이벤트(time_project null)는 null,
 * timestamp 스냅샷이 없으면 회차 id fallback.
 */
export const resolveTimeProjectBadge = (
  event: HistoryEvent
): HistoryTimeProjectBadge | null => {
  if (!event.time_project) return null;

  const { id, timestamp } = event.time_project;
  if (timestamp) return { kind: "timestamp", value: timestamp };
  return { kind: "timeProjectId", value: id };
};

/** 같은 발생 일자(표시 시간대 기준)로 묶인 이벤트 — 날짜 머리 하나와 그 아래 행들. */
export interface HistoryDateGroup {
  /** 표시 시간대 기준 `YYYY-MM-DD` */
  dateKey: string;
  /** 묶음의 첫 이벤트 발생 시각 — 날짜 머리 표기에 쓴다. */
  firstOccurredAt: string;
  events: HistoryEvent[];
}

/**
 * 이벤트를 표시 시간대 기준 발생 일자별로 묶는다. 입력 순서(최신순)를 그대로 유지하며,
 * 연속한 같은 일자만 한 묶음이 된다 — 페이지가 이어 붙어도 날짜 머리가 중복되지 않는다.
 */
export const groupHistoryEventsByDate = (
  events: HistoryEvent[],
  timezone: string
): HistoryDateGroup[] => {
  const groups: HistoryDateGroup[] = [];
  events.forEach((event) => {
    const dateKey = formatInstant(event.occurred_at, "YYYY-MM-DD", { tz: timezone });
    const last = groups[groups.length - 1];
    if (last && last.dateKey === dateKey) {
      last.events.push(event);
      return;
    }
    groups.push({ dateKey, firstOccurredAt: event.occurred_at, events: [event] });
  });
  return groups;
};
